import { Loader2, Pencil, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { InlineEditStatus } from "./useInlineEdit";

/**
 * Casca visual comum a todos os editores inline: o display normal, o lápis que
 * só aparece no hover, o spinner de gravação e o estado de erro.
 *
 * Sem `canEdit` renderiza o display cru — sem cursor, sem tabIndex, sem lápis.
 * A ausência de affordance é o que comunica "você não tem permissão"; um lápis
 * que abre e depois falha seria pior.
 */
export function InlineEditShell({
  canEdit,
  status,
  error,
  label,
  triggerProps,
  children,
  className,
  align = "left",
  disabledReason,
  hidePencil = false,
  interactive = true,
  fill = false,
}: {
  canEdit: boolean;
  status: InlineEditStatus;
  error: string | null;
  /** Nome do campo em pt-BR, para o aria-label. */
  label: string;
  triggerProps: Record<string, unknown>;
  children: React.ReactNode;
  className?: string;
  align?: "left" | "right";
  disabledReason?: string;
  /** Para displays que já têm o próprio gatilho (ex.: célula com link). */
  hidePencil?: boolean;
  /**
   * `false` quando um ancestral já é o elemento interativo — é o caso dos
   * editores com popover, onde o gatilho é do Radix. Sem isso a casca vira um
   * segundo ponto de foco dentro do primeiro, e o Tab para duas vezes na
   * mesma célula.
   */
  interactive?: boolean;
  /**
   * `true` quando a casca deve ocupar a largura do container em vez de se
   * ajustar ao conteúdo — é o caso de uma célula de tabela com largura fixa.
   *
   * Sem isso a casca é `inline-flex`, ou seja, dimensionada pelo conteúdo, e o
   * texto acaba cortado antes da borda da coluna: quem decide onde cortar
   * passa a ser um cálculo de largura intrínseca, e não o espaço disponível.
   * Com a largura definida, o corte acontece exatamente na borda da célula.
   */
  fill?: boolean;
}) {
  if (!canEdit) {
    const content = (
      <div className={cn(fill && "w-full", className)}>{children}</div>
    );
    if (!disabledReason) return content;
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent>{disabledReason}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const isSaving = status === "saving";
  const isError = status === "error";

  return (
    <div
      {...(interactive ? triggerProps : {})}
      aria-label={interactive ? `Editar ${label}` : undefined}
      aria-busy={isSaving || undefined}
      className={cn(
        "group/inline relative max-w-full items-center gap-1 rounded-sm",
        fill ? "flex w-full" : "inline-flex",
        "-mx-1 px-1 py-0.5 cursor-pointer transition-colors",
        "hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        align === "right" && "flex-row-reverse",
        isSaving && "opacity-60 pointer-events-none",
        isError && "ring-1 ring-destructive",
        className,
      )}
    >
      {/* Com largura definida o miolo pode tomar o espaço que sobra do ícone;
          sem ela, `flex-1` (base 0) zeraria a contribuição de largura e o texto
          cortaria cedo demais. */}
      <span className={cn("min-w-0 truncate", fill && "flex-1")}>{children}</span>
      {isSaving ? (
        <Loader2 className="h-3 w-3 shrink-0 animate-spin text-muted-foreground" />
      ) : isError ? (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <AlertCircle className="h-3 w-3 shrink-0 text-destructive" />
            </TooltipTrigger>
            <TooltipContent>{error ?? "Não foi possível salvar"}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : hidePencil ? null : (
        <Pencil className="h-3 w-3 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/inline:opacity-70" />
      )}
    </div>
  );
}
